import { useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: 24,
  p: 4,
};

export interface Tag {
  key: string;
  value: string;
}

interface AddModalProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  addDevice: any;
  setAddDevice: (device: any) => void;
  organizations: any[];
  submitAddDevice: (device: any) => void;
}

const AddDeviceModal = ({
  openModal,
  setOpenModal,
  addDevice,
  setAddDevice,
  organizations,
  submitAddDevice,
}: AddModalProps) => {
  const [tags, setTags] = useState<Tag[]>(addDevice.tags || []);

  const handleTagChange = (index: number, field: keyof Tag, value: string) => {
    const newTags = [...tags];
    newTags[index][field] = value;
    setTags(newTags);
    setAddDevice({ ...addDevice, tags: newTags });
  };

  const addTag = () => {
    const newTags = [...tags, { key: "", value: "" }];
    setTags(newTags);
    setAddDevice({ ...addDevice, tags: newTags });
  };

  const removeTag = (index: number) => {
    const newTags = tags.filter((_, i) => i !== index);
    setTags(newTags);
    setAddDevice({ ...addDevice, tags: newTags });
  };

  const handleDeviceTypeChange = (event: SelectChangeEvent<string>) => {
    setAddDevice({ ...addDevice, deviceType: event.target.value as string });
  };

  return (
    <Modal
      open={openModal}
      onClose={() => {
        setOpenModal(false);
        setAddDevice({
          organization: "",
          firmwareVersion: "",
          mac_address: "",
          tags: [],
          deviceType: "",
        });
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box component="form" sx={style} noValidate autoComplete="off">
        <Typography
          id="modal-modal-title"
          variant="h5"
          component="h2"
          marginBottom={"20px"}
        >
          Add Device Details
        </Typography>
        <TextField
          id="mac_address"
          label="Mac Address"
          value={addDevice.mac_address}
          sx={{ width: "100%", marginBottom: "10px" }}
          onChange={(e) =>
            setAddDevice({ ...addDevice, mac_address: e.target.value })
          }
        />
        <TextField
          id="firmwareVersion"
          label="Firmware Version"
          value={addDevice.firmwareVersion}
          sx={{ width: "100%", marginBottom: "10px" }}
          onChange={(e) =>
            setAddDevice({
              ...addDevice,
              firmwareVersion: e.target.value,
            })
          }
        />
        <FormControl fullWidth style={{ marginBottom: "10px" }}>
          <InputLabel id="device-type-label">Device Type</InputLabel>
          <Select
            labelId="device-type-label"
            id="device-type"
            value={addDevice?.deviceType}
            onChange={handleDeviceTypeChange}
            label="Device Type"
          >
            <MenuItem value="ESP32">ESP32</MenuItem>
            <MenuItem value="1">Device Type 1</MenuItem>
            <MenuItem value="2">Device Type 2</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ marginBottom: "10px" }}>
          <InputLabel id="demo-simple-select-label">
            Select Organization
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={addDevice.organization}
            label="Select Organization"
            onChange={(e) =>
              setAddDevice({
                ...addDevice,
                organization: e.target.value,
              })
            }
          >
            {organizations?.map((data: any) => (
              <MenuItem key={data.name} value={data.id}>
                {data.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="h6" gutterBottom>
          Tags
        </Typography>
        {tags.map((tag, index) => (
          <Box
            key={index}
            sx={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
          >
            <TextField
              label="Key"
              value={tag.key}
              onChange={(e) => handleTagChange(index, "key", e.target.value)}
              sx={{ flex: 1, marginRight: "10px" }}
            />
            <TextField
              label="Value"
              value={tag.value}
              onChange={(e) => handleTagChange(index, "value", e.target.value)}
              sx={{ flex: 1, marginRight: "10px" }}
            />
            <Button
              variant="outlined"
              color="error"
              onClick={() => removeTag(index)}
            >
              Remove
            </Button>
          </Box>
        ))}
        <Button
          variant="contained"
          onClick={addTag}
          sx={{ marginBottom: "20px" }}
        >
          Add Tag
        </Button>

        <Grid container justifyContent={"end"}>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => setOpenModal(false)}
            sx={{ marginTop: "20px" }}
          >
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => {
              submitAddDevice({ ...addDevice, tags });
              setOpenModal(false);
            }}
            sx={{ marginLeft: "20px", marginTop: "20px" }}
          >
            Add
          </Button>
        </Grid>
      </Box>
    </Modal>
  );
};

export default AddDeviceModal;
